import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import { AppShell, TopbarPending } from "@/components/layout/app-shell";
import { ContactLine, NotFoundState, StandaloneFrame, StateMessage } from "@/components/page-states";
import { useReloadIfStale } from "@shared/client/route-error";
import { usePageTitle } from "@shared/client/view";
import { Button } from "@shared/components/ui/button";
import { useT } from "@shared/client/locale";

/**
 * Ce que montre l'app quand un écran plante au rendu (`errorElement` du
 * routeur) : ce qui s'est passé en clair, la reprise, et à qui écrire. Jamais
 * de trace à l'écran — elle part dans la console. Un onglet resté ouvert
 * pendant un déploiement se recharge d'abord, une fois. `inShell` : sous /app, le
 * message s'affiche dans le cadre de l'espace, rail compris (un lien du rail
 * quitte l'erreur) ; ailleurs, sur une page à part. La Vue d'ensemble n'est
 * proposée qu'à qui vient de l'espace ou de l'onboarding : une page publique
 * (désinscription) n'envoie pas vers la connexion.
 */
export default function RouteErrorPage({ inShell = false }: { inShell?: boolean }) {
  const error = useRouteError();
  const { pathname } = useLocation();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const fromProduct = /^\/(app|onboarding)(\/|$)/.test(pathname);
  const t = useT();
  const copy = t.common.states;
  usePageTitle(notFound ? t.common.pageTitles.notFound : t.common.pageTitles.error);

  // Après un déploiement, l'erreur vient presque sûrement de l'ancien front :
  // l'onglet se recharge une fois (`shared/client/route-error.tsx`), sans rien
  // afficher entre-temps. Une adresse introuvable n'est pas un plantage.
  const checking = useReloadIfStale(error, !notFound);
  if (checking) return null;

  const placement = inShell ? "content" : "page";
  const message = notFound ? (
    <NotFoundState placement={placement} />
  ) : (
    <StateMessage
      actions={
        <>
          <Button onClick={() => window.location.reload()} type="button">
            {copy.reload}
          </Button>
          {/* Lien natif : un chargement complet repart d'un état propre. */}
          {fromProduct && (
            <Button asChild variant="outline">
              <a href="/app">{copy.overview}</a>
            </Button>
          )}
        </>
      }
      placement={placement}
      title={copy.renderError.title}
    >
      <p>{copy.renderError.body}</p>
      <ContactLine />
    </StateMessage>
  );

  if (inShell) {
    return (
      <AppShell email="" name="" pending={<TopbarPending state="hidden" />}>
        {message}
      </AppShell>
    );
  }

  return <StandaloneFrame>{message}</StandaloneFrame>;
}
